/**
 * Presentation Layer - Animations
 * Re-exports shared animation variants from constants
 * and provides any presentation-layer specific animations
 */

// Re-export all animation constants for convenience
export {
  // Easing & Duration
  EASING,
  DURATION,
  TRANSITION,
  // Collapsible
  collapsibleVariants,
  collapsibleFastVariants,
  // Fade
  fadeVariants,
  fadeScaleVariants,
  fadeScaleSmallVariants,
  // Slide
  slideLeftVariants,
  slideRightVariants,
  slideUpVariants,
  slideDownVariants,
  // Stagger
  staggerContainerVariants,
  staggerContainerFastVariants,
  staggerItemVariants,
  staggerItemCustomVariants,
  staggerItemVerticalVariants,
  // Rotation
  rotateChevronTransition,
  rotateVariants,
  rotate90Variants,
  // Modal/Overlay
  overlayVariants,
  modalVariants,
  // Sidebar
  sidebarLeftVariants,
  sidebarRightVariants,
  // Tooltip
  tooltipVariants,
  // Button
  buttonTapVariants,
  buttonHoverVariants,
  iconButtonVariants,
  // Utils
  getStaggerDelay,
  createTransition,
} from "@/constants/animations";

// Import constants for backward compatibility
import { ANIMATION_VALUES, ANIMATION_DURATIONS } from "@/constants";
import type { Variants } from "framer-motion";

// ============================================================================
// LEGACY VARIANTS (for backward compatibility)
// These use the old ANIMATION_VALUES/ANIMATION_DURATIONS constants
// Consider migrating to the new variants from @/constants/animations
// ============================================================================

/**
 * @deprecated Use slideUpVariants from @/constants/animations instead
 */
export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: ANIMATION_VALUES.OFFSET_Y,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.FADE,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use slideDownVariants from @/constants/animations instead
 */
export const fadeInDown: Variants = {
  hidden: {
    opacity: 0,
    y: ANIMATION_VALUES.OFFSET_Y_NEGATIVE,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: ANIMATION_DURATIONS.FADE,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use fadeVariants from @/constants/animations instead
 */
export const fadeIn: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: ANIMATION_DURATIONS.QUICK,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use fadeScaleVariants from @/constants/animations instead
 */
export const scaleIn: Variants = {
  hidden: {
    opacity: 0,
    scale: ANIMATION_VALUES.SCALE_DOWN,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: ANIMATION_DURATIONS.FAST,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use staggerContainerVariants from @/constants/animations instead
 */
export const staggerChildren: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: ANIMATION_DURATIONS.STAGGER,
    },
  },
};

/**
 * @deprecated Use slideLeftVariants from @/constants/animations instead
 */
export const slideInLeft: Variants = {
  hidden: {
    opacity: 0,
    x: ANIMATION_VALUES.OFFSET_X,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATIONS.QUICK,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use slideRightVariants from @/constants/animations instead
 */
export const slideInRight: Variants = {
  hidden: {
    opacity: 0,
    x: ANIMATION_VALUES.OFFSET_X_POSITIVE,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: ANIMATION_DURATIONS.QUICK,
      ease: "easeOut",
    },
  },
};

/**
 * @deprecated Use buttonTapVariants from @/constants/animations instead
 */
export const buttonTap = {
  scale: ANIMATION_VALUES.TAP_SCALE,
};

/**
 * @deprecated Use buttonHoverVariants from @/constants/animations instead
 */
export const buttonHover = {
  scale: ANIMATION_VALUES.HOVER_SCALE,
};
